import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { FiLayers, FiSearch, FiPlus, FiRefreshCcw, FiUsers, FiBook } from 'react-icons/fi'
import Button from '../../components/Button/Button'
import { fetchBatches } from '../../store/slices/adminSlice'
import adminApi from '../../api/adminApi'
import Table from '../../components/Table/Table'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import './Batches.css'

const Batches = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { batches, isLoading, error } = useSelector((state) => state.admin)
  
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)

  useEffect(() => {
    loadBatches()
  }, [page, statusFilter])

  const loadBatches = async () => {
    const params = {
      page,
      limit: 25,
    }
    if (statusFilter !== 'all') {
      params.status = statusFilter
    }
    dispatch(fetchBatches(params))
  }

  const filteredBatches = useMemo(() => {
    const batchesList = batches?.batches || batches?.data || []
    if (!Array.isArray(batchesList)) return []
    const query = searchQuery.toLowerCase()
    return batchesList.filter((batch) => {
      const matchesSearch =
        !query ||
        [batch.batchName, batch.course?.title]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))
      return matchesSearch
    })
  }, [batches, searchQuery])

  const columns = [
    {
      key: 'name',
      label: 'Batch',
      render: (row) => (
        <div>
          <strong>{row.batchName}</strong>
          <span className="batch-course">{row.course?.title || 'No course'}</span>
        </div>
      ),
    },
    {
      key: 'students',
      label: 'Students',
      render: (row) => (
        <div className="batch-metric">
          <FiUsers size={16} />
          <span>{row.students?.length || 0}</span>
        </div>
      ),
    },
    {
      key: 'teachers',
      label: 'Teachers',
      render: (row) => (
        <div className="batch-metric">
          <FiUsers size={16} />
          <span>{row.teachers?.length || 0}</span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status || 'upcoming'} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <Link to={`/batches/${row.id || row._id}`} className="action-link">
          View Details
        </Link>
      ),
    },
  ]

  return (
    <div className="batches-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Batch Management</p>
          <h1>All Batches</h1>
          <p className="subhead">Manage batches and assignments</p>
        </div>
        <div className="header-actions">
          <Button onClick={() => navigate('/batches/new')}>
            <FiPlus size={18} />
            Create Batch
          </Button>
          <button
            type="button"
            className="refresh-btn"
            onClick={loadBatches}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </header>

      <section className="toolbar">
        <div className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search batches"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="upcoming">Upcoming</option>
          <option value="ongoing">Ongoing</option>
          <option value="completed">Completed</option>
        </select>
      </section>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={filteredBatches}
        loading={isLoading}
        emptyMessage="No batches found"
      />
    </div>
  )
}

export default Batches

